import React from 'react';
import { View, StyleSheet, ViewStyle, StyleProp } from 'react-native';
import { Clickable } from './Clickable';
import { appTheme, useThemeColors } from '../../shared/theme';
import { androidResolveShadow } from '../../shared/theme/sharedStyles';

export type ContentCardVariant = 'default' | 'surface' | 'elevated' | 'outlined';
export type ContentCardPadding = 'none' | 'sm' | 'xl' | 'lg';

export interface ContentCardProps {
  children: React.ReactNode;
  onPress?: () => void;
  padding?: ContentCardPadding;
  variant?: ContentCardVariant;
  /** Top accent bar: true = primary color, or pass a color string */
  accentTop?: boolean | string;
  style?: StyleProp<ViewStyle>;
  /** Override border color (default: primary + '22') */
  borderColor?: string;
  /** Surface + shadow only — no hairline (v3 studio). Default false. */
  borderless?: boolean;
}

export const ContentCard: React.FC<ContentCardProps> = ({ children, onPress, padding = 'xl', variant = 'default', accentTop, style, borderColor: border_color_prop, borderless = false }) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const card = appTheme.card;
  const border_color = border_color_prop ?? colors.primary + '22';
  const background_color =
    variant === 'elevated'
      ? colors.surface
      : variant === 'outlined'
        ? 'transparent'
        : colors.surface;
  const shadow_style = variant === 'outlined' ? {} : androidResolveShadow({ ...card.shadow, shadowColor: colors.shadowColor });
  const padding_value = padding === 'none' ? 0 : card.padding[padding as keyof typeof card.padding];

  const container_style: StyleProp<ViewStyle> = [
    styles.base,
    {
      borderRadius: card.radius,
      padding: padding_value,
      backgroundColor: background_color,
      ...(borderless
        ? { borderWidth: 0, borderColor: 'transparent' as const }
        : { borderWidth: 1, borderColor: border_color }),
      ...shadow_style,
    },
    ...(style ? [style] : []),
  ];

  const accent_color = typeof accentTop === 'string' ? accentTop : colors.primary;

  const content = accentTop ? (
    <>
      <View style={[styles.accent, { backgroundColor: accent_color }]} />
      <View style={styles.inner}>{children}</View>
    </>
  ) : (
    children
  );

  if (onPress) {
    return (
      <Clickable onPress={onPress} style={container_style} accessibilityRole="button">
        {content}
      </Clickable>
    );
  }

  return <View style={container_style}>{content}</View>;
};

const styles = StyleSheet.create({
  base: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  },
  accent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
  },
  inner: {},
});

export default ContentCard;
